import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
} from '@mui/material';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import { TeamMember, User } from '../../types';
import { getFirstName } from '../../utils/sortByFirstName';

interface TransferHeadCoachDialogProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  onTransferComplete: () => void;
}

const TransferHeadCoachDialog: React.FC<TransferHeadCoachDialogProps> = ({
  open,
  onClose,
  teamId,
  onTransferComplete,
}) => {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [memberUsers, setMemberUsers] = useState<User[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(true);

  useEffect(() => {
    if (open && teamId) {
      loadMembers();
    }
  }, [open, teamId]);

  const loadMembers = async () => {
    if (!user) return;
    
    setLoadingMembers(true);
    setError('');
    
    try {
      // Get all team members
      const teamMembers = await teamService.getTeamMembers(teamId);
      
      // Get current head coach ID
      const headCoachId = await teamService.getHeadCoachId(teamId);
      
      // Filter out current head coach
      const eligible = teamMembers.filter(
        member => member.athleteId !== headCoachId
      );
      const userPromises = eligible.map(member =>
        userService.getUserById(member.athleteId)
      );
      const users = await Promise.all(userPromises);
      const pairs = eligible
        .map((member, i) => ({ member, user: users[i] }))
        .filter((p): p is { member: TeamMember; user: User } => p.user != null);
      pairs.sort((a, b) =>
        getFirstName(a.user.displayName).localeCompare(getFirstName(b.user.displayName), undefined, { sensitivity: 'base' })
      );
      setMembers(pairs.map(p => p.member));
      setMemberUsers(pairs.map(p => p.user));
    } catch (err: any) {
      setError(err.message || 'Failed to load team members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedMemberId) {
      setError('Please select a team member');
      return;
    }
    
    if (confirmationText !== 'CONFIRM') {
      setError('Please type CONFIRM to confirm the transfer');
      return;
    }
    
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Find the selected member
      const selectedMember = members.find(m => m.athleteId === selectedMemberId);
      if (!selectedMember) {
        throw new Error('Selected member not found');
      }
      
      await teamService.assignHeadCoachRole(teamId, selectedMemberId, user.id);
      
      setSelectedMemberId('');
      setConfirmationText('');
      onTransferComplete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to transfer head coach role');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedMemberId('');
      setConfirmationText('');
      setError('');
      onClose();
    }
  };

  const getMemberDisplayName = (memberId: string): string => {
    const member = members.find(m => m.athleteId === memberId);
    if (!member) return '';
    
    const memberUser = memberUsers.find(u => u.id === member.athleteId);
    return memberUser?.displayName || member.athleteId;
  };

  const getMemberRoleBadge = (member: TeamMember): string => {
    if (member.isAdmin) {
      return member.role === 'headCoach' ? 'Head Coach (Admin)' :
             member.role === 'coach' ? 'Coach (Admin)' :
             'Athlete (Admin)';
    }
    return member.role === 'headCoach' ? 'Head Coach' :
           member.role === 'coach' ? 'Coach' :
           'Athlete';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Transfer Head Coach Role</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ pt: 1 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Warning:</strong> This will transfer the head coach role to another team member.
              </Typography>
              <Typography variant="body2">
                You will become a regular coach after the transfer. Only admins and the new head coach will be able to transfer the role again.
              </Typography>
            </Alert>
            
            {loadingMembers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : members.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No eligible team members found. You need at least one other team member to transfer the head coach role.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Select New Head Coach</InputLabel>
                  <Select
                    value={selectedMemberId}
                    onChange={(e) => setSelectedMemberId(e.target.value)}
                    label="Select New Head Coach"
                    disabled={loading}
                  >
                    {members.map((member) => {
                      const memberUser = memberUsers.find(u => u.id === member.athleteId);
                      return (
                        <MenuItem key={member.id} value={member.athleteId}>
                          {memberUser?.displayName || member.athleteId}
                          {member.role && (
                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                              ({getMemberRoleBadge(member)})
                            </Typography>
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                
                {selectedMemberId && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        You are about to transfer the head coach role to{' '}
                        <strong>{getMemberDisplayName(selectedMemberId)}</strong>.
                      </Typography>
                    </Alert>
                  </Box>
                )}
                
                <TextField
                  fullWidth
                  label="Type CONFIRM to proceed"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading || !selectedMemberId}
                  helperText="You must type 'CONFIRM' exactly to confirm the transfer"
                  error={confirmationText !== '' && confirmationText !== 'CONFIRM'}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="warning"
            disabled={loading || !selectedMemberId || confirmationText !== 'CONFIRM' || loadingMembers || members.length === 0}
          >
            {loading ? <CircularProgress size={20} /> : 'Transfer Head Coach Role'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TransferHeadCoachDialog;

